// src/pages/kanban.ts
import { BACKEND } from '../config';
import { apiForm, apiJson, primeCsrf } from '../lib/apiClient';

export { primeCsrf };

/** Базовые типы */
export type ID = number;
export type Role = 'admin' | 'manager' | 'employee' | 'representative_employee' | 'chairman';
export type Priority = 'low' | 'med' | 'high' | 'crit';
export type Period = '' | 'daily' | 'weekly' | 'monthly' | 'ndays' | 'workdays';

export interface UserBrief {
  id: ID;
  username: string;
  first_name: string;
  last_name: string;
  role: Role;
}

export interface Group {
  id: ID;
  name: string;
  comment: string;
  order: number;
  created_by: ID;
  created_at: string;
  members: UserBrief[];
}

export interface Column {
  id: ID;
  group: ID;
  name: string;
  comment: string;
  system_type: '' | 'todo' | 'in_progress' | 'done' | 'overdue';
  allow_return: boolean;
  recurring: boolean;
  default_period: Period;
  default_period_value?: number | null;
  order: number;
  created_at: string;
  /** Ручной цвет верхней полоски колонки (#rrggbb); '' = авто по system_type. Меняют только admin/manager. */
  color?: string;
}

export interface Task {
  id: ID;
  group: ID;
  column: ID;
  title: string;
  description: string;
  priority: Priority;
  due_at?: string | null;
  created_by: ID;
  assignees: UserBrief[];
  order: number;
  created_at: string;
  done_at?: string | null;
  period: Period;
  period_value?: number | null;
  /** Ручной цвет верхней полоски карточки (#rrggbb); '' = авто по статусу. Меняют только admin/manager. */
  color?: string;
}

export interface ColumnComment {
  id: ID;
  column: ID;
  author: UserBrief;
  body: string;
  created_at: string;
}

export interface TaskHistory {
  id: ID;
  task: ID;
  actor: ID | UserBrief;
  action: string;
  payload?: any;
  created_at: string;
}

export interface TaskAttachment {
  id: ID;
  task: ID;
  file_name: string;
  file_size: number;
  file_url: string;
  uploaded_by: UserBrief;
  created_at: string;
}

export interface TaskChatMessage {
  id: ID;
  task: ID;
  author: UserBrief;
  body: string;
  created_at: string;
}

/* ===================== Transport ===================== */

async function http<T = any>(url: string, opts?: RequestInit): Promise<T> {
  return apiJson<T>(url, opts);
}

async function httpForm<T = any>(url: string, opts?: RequestInit): Promise<T> {
  return apiForm<T>(url, opts);
}

/* ===================== Канбан API ===================== */
export interface DirectMessageServer {
  id: ID;
  sender: UserBrief;
  recipient: UserBrief;
  body: string;
  created_at: string;
}
export type TaskChangesResponse = {
  changed: boolean;
  latest_event_at: string | null;
};

type Paginated<T> = { results?: T[] };

const unwrapList = <T,>(payload: T[] | Paginated<T> | null | undefined): T[] => {
  if (Array.isArray(payload)) return payload;
  if (payload && Array.isArray((payload as Paginated<T>).results))
    return (payload as Paginated<T>).results as T[];
  return [];
};

export type NotificationItem = {
  id: number;
  type: string;
  title: string;
  message: string;
  payload?: any;
  created_at: string;
  is_read: boolean;
};

const API = `${BACKEND}/api/kanban`;

/** Вспомогалка для querystring из объекта */
function qs(params: Record<string, string | number | boolean | undefined | null>) {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null || v === '') return;
    q.set(k, String(v));
  });
  const s = q.toString();
  return s ? `?${s}` : '';
}

/* ===== USERS (поддержка обоих стилей вызова) ===== */
type ListUsersOptions = { roles?: Role[]; exclude_group?: ID; q?: string };

function _listUsers(options?: ListUsersOptions): Promise<UserBrief[]>;
function _listUsers(roles?: Role[], exclude_group?: ID, q?: string): Promise<UserBrief[]>;
function _listUsers(a?: ListUsersOptions | Role[], b?: ID, c?: string): Promise<UserBrief[]> {
  const opts: ListUsersOptions = Array.isArray(a)
    ? { roles: a as Role[], exclude_group: b, q: c }
    : a || {};

  const p = new URLSearchParams();
  if (opts.roles?.length) p.set('roles', opts.roles.join(','));
  if (opts.exclude_group) p.set('exclude_group', String(opts.exclude_group));
  if (opts.q) p.set('q', opts.q);

  const suffix = p.toString();
  return http<UserBrief[] | Paginated<UserBrief>>(
    `${API}/users/${suffix ? `?${suffix}` : ''}`
  ).then(unwrapList);
}

export const KanbanAPI = {
  /* ===== USERS ===== */
  listUsers: _listUsers,

  /* ===== GROUPS ===== */
  listGroups: () => http<Group[] | Paginated<Group>>(`${API}/groups/`).then(unwrapList),

  createGroup: (payload: { name: string; comment?: string }) =>
    http<Group>(`${API}/groups/`, { method: 'POST', body: JSON.stringify(payload) }),

  updateGroup: (id: ID, patch: Partial<Group>) =>
    http<Group>(`${API}/groups/${id}/`, { method: 'PATCH', body: JSON.stringify(patch) }),

  reorderGroups: (group_ids: ID[]) =>
    http<{ ok: boolean }>(`${API}/groups/reorder/`, {
      method: 'POST',
      body: JSON.stringify({ group_ids }),
    }),

  deleteGroup: (id: ID) => http(`${API}/groups/${id}/`, { method: 'DELETE' }),

  // Члены группы
  addMember: (groupId: ID, user_id: ID) =>
    http(`${API}/groups/${groupId}/members/`, {
      method: 'POST',
      body: JSON.stringify({ user_id }),
    }),

  removeMember: (groupId: ID, user_id: ID) =>
    http(`${API}/groups/${groupId}/members/${user_id}/`, { method: 'DELETE' }),

  /* ===== COLUMNS ===== */
  listColumns: (groupId: ID) =>
    http<Column[] | Paginated<Column>>(`${API}/columns/${qs({ group: groupId })}`).then(unwrapList),

  createColumn: (payload: Partial<Column>) =>
    http<Column>(`${API}/columns/`, { method: 'POST', body: JSON.stringify(payload) }),

  updateColumn: (id: ID, patch: Partial<Column>) =>
    http<Column>(`${API}/columns/${id}/`, { method: 'PATCH', body: JSON.stringify(patch) }),

  reorderColumns: (column_ids: ID[]) =>
    http<{ ok: boolean }>(`${API}/columns/reorder/`, {
      method: 'POST',
      body: JSON.stringify({ column_ids }),
    }),

  deleteColumn: (id: ID) => http(`${API}/columns/${id}/`, { method: 'DELETE' }),

  /* ===== COLUMN COMMENTS ===== */
  listColComments: (columnId: ID) =>
    http<ColumnComment[] | Paginated<ColumnComment>>(
      `${API}/column-comments/${qs({ column: columnId })}`
    ).then(unwrapList),

  addColComment: (payload: { column: ID; body: string }) =>
    http<ColumnComment>(`${API}/column-comments/`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  /* ===== TASKS ===== */
  listTasks: (params: Record<string, string | number | undefined>) =>
    http<Task[] | Paginated<Task>>(`${API}/tasks/${qs(params)}`).then(unwrapList),

  checkTaskChanges: (groupId: ID, since?: string | null) =>
    http<TaskChangesResponse>(
      `${API}/tasks/changes/${qs({ group: groupId, since: since || undefined })}`
    ),
  taskChangesStreamUrl: (groupId: ID) => `${API}/tasks/stream/${qs({ group: groupId })}`,

  createTask: (payload: Partial<Task> & { assignee_ids?: ID[] }) => {
    const clean: Record<string, any> = {};
    Object.entries(payload).forEach(([k, v]) => {
      const isEmptyArray = Array.isArray(v) && v.length === 0;
      if (v === undefined || v === null || (isEmptyArray && k !== 'assignee_ids')) return;
      clean[k] = v;
    });
    return http<Task>(`${API}/tasks/`, { method: 'POST', body: JSON.stringify(clean) });
  },

  updateTask: (id: ID, patch: Partial<Task> & { assignee_ids?: ID[] }) => {
    const clean: Record<string, any> = {};
    Object.entries(patch).forEach(([k, v]) => {
      const isEmptyArray = Array.isArray(v) && v.length === 0;
      if (v === undefined || v === null || (isEmptyArray && k !== 'assignee_ids')) return;
      clean[k] = v;
    });
    return http<Task>(`${API}/tasks/${id}/`, { method: 'PATCH', body: JSON.stringify(clean) });
  },

  deleteTask: (id: ID) => http(`${API}/tasks/${id}/`, { method: 'DELETE' }),

  moveTask: (id: ID, target_column: ID, target_order = 0) =>
    http<Task>(`${API}/tasks/${id}/move/`, {
      method: 'POST',
      body: JSON.stringify({ target_column, target_order }),
    }),

  bulk: (payload: any) =>
    http<{ ok: boolean } | Record<string, any>>(`${API}/tasks/bulk/`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  history: (taskId: ID) =>
    http<TaskHistory[] | Paginated<TaskHistory>>(`${API}/history/${qs({ task: taskId })}`).then(
      unwrapList
    ),

  listTaskAttachments: (taskId: ID) =>
    http<TaskAttachment[] | Paginated<TaskAttachment>>(`${API}/tasks/${taskId}/attachments/`).then(
      unwrapList
    ),

  deleteTaskAttachment: (taskId: ID, attachmentId: ID) =>
    http(`${API}/tasks/${taskId}/attachments/${attachmentId}/`, { method: 'DELETE' }),

  listDirectMessages: (withUserId: ID, after?: string) =>
    http<DirectMessageServer[] | Paginated<DirectMessageServer>>(
      `${API}/direct-messages/${qs({ with: withUserId, after: after || '' })}`
    ).then(unwrapList),

  sendDirectMessage: (to: ID, body: string) =>
    http<DirectMessageServer>(`${API}/direct-messages/`, {
      method: 'POST',
      body: JSON.stringify({ to, body }),
    }),

  uploadTaskAttachment: (taskId: ID, file: File) => {
    const body = new FormData();
    body.append('file', file);
    return httpForm<TaskAttachment | TaskAttachment[]>(`${API}/tasks/${taskId}/attachments/`, {
      method: 'POST',
      body,
    });
  },

  listTaskChatMessages: (taskId: ID) =>
    http<TaskChatMessage[] | Paginated<TaskChatMessage>>(`${API}/tasks/${taskId}/chat/`).then(
      unwrapList
    ),

  addTaskChatMessage: (taskId: ID, body: string) =>
    http<TaskChatMessage>(`${API}/tasks/${taskId}/chat/`, {
      method: 'POST',
      body: JSON.stringify({ body }),
    }),

  updateTaskChatMessage: (taskId: ID, messageId: ID, body: string) =>
    http<TaskChatMessage>(`${API}/tasks/${taskId}/chat/${messageId}/`, {
      method: 'PATCH',
      body: JSON.stringify({ body }),
    }),
  /* ================= NOTIFICATIONS ================= */

  listNotifications: (unread = 1) =>
    http<NotificationItem[] | Paginated<NotificationItem>>(
      `${API}/notifications/?unread=${unread}`
    ).then(unwrapList),

  ackNotification: (id: ID, response: 'ok' | 'not_ok') =>
    http(`${API}/notifications/${id}/ack/`, {
      method: 'POST',
      body: JSON.stringify({ response }),
    }),
};
